import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SEO, generateOrganizationSchema, generateLocalBusinessSchema, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Schema.org structured data
  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      generateOrganizationSchema(),
      generateLocalBusinessSchema(),
      generateWebPageSchema(
        '株式会社ツーサム | 東京都北区のビジネスソリューション企業',
        '株式会社ツーサムは東京都北区赤羽に拠点を置く総合ビジネスソリューション企業です。プリントサービス、ビジネスコンサルティング、デジタルマーケティング、プロモーション事業を通じて、中小企業のデジタル変革と成長を支援します。',
        siteUrl
      )
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      rating: 4.9,
      text: '「このサービスを導入してから、業務効率が大幅に向上しました。特にクラウド管理システムの使いやすさには驚きました。スタッフ全員がすぐに使いこなせるようになり、コスト削減にも大きく貢献しています。」',
      name: '田中 太郎',
      position: '代表取締役社長',
      company: '株式会社サンプル商事',
      avatar: 'https://public.readdy.ai/ai/img_res/4f25da0384b693818c55ed2164d5c0c5.jpg'
    },
    {
      rating: 5.0,
      text: '「導入前は不安もありましたが、サポート体制が非常に充実しており、安心して利用できています。特にセキュリティ面での配慮が素晴らしく、お客様の情報を安全に管理できるようになりました。」',
      name: '佐藤 花子',
      position: '経営企画部長',
      company: '山田製造株式会社',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20japanese%20business%20woman%20in%20elegant%20suit%20smiling%20warmly%20in%20bright%20modern%20office%20with%20minimalist%20white%20background%20corporate%20portrait&width=80&height=80&seq=testimonial2&orientation=squarish'
    },
    {
      rating: 4.8,
      text: '「カスタマイズ性が高く、当社の業務フローに完璧にフィットしました。導入後3ヶ月で売上が20%向上し、顧客満足度も大きく改善されました。投資対効果は期待以上です。」',
      name: '鈴木 一郎',
      position: 'IT部門マネージャー',
      company: '東京テクノロジー株式会社',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20japanese%20technology%20manager%20in%20business%20casual%20attire%20smiling%20confidently%20in%20contemporary%20office%20with%20pure%20white%20background%20modern%20portrait&width=80&height=80&seq=testimonial3&orientation=squarish'
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5sc00fc87p5i2kb2jr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleFooterLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="株式会社ツーサム | ビジネスの可能性を最大化する、共創ソリューション。"
        description="株式会社ツーサムは東京都北区赤羽に拠点を置く総合ビジネスソリューション企業です。プリントサービス、ビジネスコンサルティング、デジタルマーケティング、プロモーション事業を通じて、中小企業のデジタル変革と成長を支援します。"
        keywords="株式会社ツーサム,ビジネスソリューション,プリントサービス,ビジネスコンサルティング,デジタルマーケティング,東京都北区,赤羽"
        canonical={siteUrl}
        ogType="website"
        schema={combinedSchema}
      />
      
      {/* ナビゲーション */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2 transform transition-transform duration-300 hover:scale-105">
              <div className="bg-transparent rounded-lg p-2 sm:p-3">
                <img 
                  src="https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png" 
                  alt="株式会社ツーサム" 
                  className="h-16 sm:h-20 md:h-24 w-auto"
                />
              </div>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-gray-700 hover:text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer transform hover:-translate-y-0.5">
                会社概要
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
              </a>
              <Link to="/business" className="text-gray-700 hover:text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer transform hover:-translate-y-0.5">
                事業内容
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <a href="#contact" className="text-gray-700 hover:text-gray-900 transition-all duration-300 relative group whitespace-nowrap cursor-pointer transform hover:-translate-y-0.5">
                お問い合わせ
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gray-900 transition-all duration-300 group-hover:w-full"></span>
              </a>
            </div>
            
            {/* モバイルメニューボタン */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-800 hover:text-gray-900 transition-colors duration-300 cursor-pointer"
              aria-label="メニュー"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* モバイルメニュー */}
        <div className={`md:hidden bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 sm:px-6 py-4 space-y-4">
            <a 
              href="#about" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 cursor-pointer py-2"
            >
              会社概要
            </a>
            <Link 
              to="/business" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 cursor-pointer py-2"
            >
              事業内容
            </Link>
            <a 
              href="#contact" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 cursor-pointer py-2"
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </nav>

      {/* ヒーローセクション */}
      <section className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6" id="hero" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className={`space-y-4 sm:space-y-6 transition-all duration-1000 ${visibleSections.has('hero') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
              <p className="text-xs sm:text-sm text-gray-600 tracking-wider animate-fade-in">ビジネスの可能性を最大化する、共創ソリューション。</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif leading-tight">
                ビジネスの<span className="text-teal-600 inline-block transition-all duration-300 hover:scale-110">成長</span>を<br />
                加速させる<br />
                <span className="text-teal-600 inline-block transition-all duration-300 hover:scale-110">トータルパートナー</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                株式会社ツーサムはクライアントとの良きパートナーシップを通し、新しい価値を創造し続けていきます。
              </p>
            </div>
            <div className={`relative transition-all duration-1000 delay-300 ${visibleSections.has('hero') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform transition-all duration-500 hover:scale-105 hover:shadow-3xl">
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=premium%20japanese%20corporate%20executives%20in%20luxury%20glass%20office%20tower%20meeting%20room%20with%20panoramic%20city%20views%20sophisticated%20business%20professionals%20discussing%20strategy%20around%20mahogany%20conference%20table%20with%20state%20of%20the%20art%20technology%20screens%20ambient%20natural%20lighting%20ultra%20professional%20photography%20highest%20quality%20resolution&width=600&height=700&seq=herohd2&orientation=portrait" 
                    alt="ビジネスソリューション"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-teal-600/10 to-transparent"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 w-24 h-24 sm:w-32 sm:h-32 bg-teal-100 rounded-full opacity-50 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* 会社概要セクション */}
      <section id="about" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 sm:gap-12 md:gap-16 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.has('about') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px]">
                  <img 
                    src="https://readdy.ai/api/search-image?query=stunning%20tokyo%20cityscape%20panoramic%20view%20with%20modern%20skyscrapers%20and%20urban%20architecture%20beautiful%20metropolitan%20skyline%20during%20golden%20hour%20vibrant%20city%20lights%20and%20buildings%20professional%20urban%20photography%20clear%20sky%20natural%20lighting&width=600&height=700&seq=tokyocity2&orientation=portrait" 
                    alt="会社概要"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className={`space-y-6 sm:space-y-8 transition-all duration-1000 delay-300 ${visibleSections.has('about') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
              <div>
                <p className="text-xs sm:text-sm text-teal-600 tracking-wider mb-2 sm:mb-3">会社概要</p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-3 sm:mb-4">
                  信頼と実績で<br />
                  ビジネスを支える
                </h2>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                  私たちは、総合ビジネスソリューション企業です。プリントサービスからデジタルマーケティング、ビジネスコンサルティングまで、幅広いサービスを提供しています。
                </p>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  お客様のビジネス課題に寄り添い、最適なソリューションを提案することで、持続的な成長をサポートします。豊富な実績と専門知識を活かし、貴社のビジネスパートナーとして共に歩んでまいります。
                </p>
              </div>

              <div className="pt-4">
                <h4 className="font-semibold text-gray-800 mb-4 text-sm sm:text-base">会社情報</h4>
                <div className="space-y-3 text-xs sm:text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-building-line text-teal-600"></i>
                    </div>
                    <div>
                      <strong className="text-gray-800">会社名：</strong>株式会社ツーサム
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-user-line text-teal-600"></i>
                    </div>
                    <div>
                      <strong className="text-gray-800">代表者名:</strong>代表取締役社長 中林恵
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-calendar-line text-teal-600"></i>
                    </div>
                    <div>
                      <strong className="text-gray-800">設立年月日：</strong>2000年6月
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-money-yen-circle-line text-teal-600"></i>
                    </div>
                    <div>
                      <strong className="text-gray-800">資本金：</strong>1,000万円
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-briefcase-line text-teal-600"></i>
                    </div>
                    <div>
                      <strong className="text-gray-800">主要取引先：</strong>大手製造業、小売業、サービス業など多数
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サービスセクション */}
      <section id="services" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gray-50" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${visibleSections.has('services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-xs sm:text-sm text-teal-600 tracking-wider mb-2 sm:mb-3">私たちのサービス</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-3 sm:mb-4">
              企業の課題を解決する<br />
              包括的なソリューション
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4">
              リアルとデジタルの融合で、貴社のビジネス成長をトータルサポートします
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {[
              {
                bg: 'bg-blue-50',
                img: 'https://readdy.ai/api/search-image?query=modern%20professional%20printing%20press%20machine%20in%20clean%20industrial%20facility%20with%20high%20quality%20commercial%20printer%20producing%20colorful%20business%20documents%20and%20marketing%20materials%20organized%20workspace%20with%20paper%20stacks%20professional%20printing%20service%20environment%20realistic%20photography%20bright%20lighting&width=300&height=256&seq=printservice1&orientation=squarish',
                title: 'プリントサービス',
                delay: 'delay-100'
              },
              {
                bg: 'bg-green-50',
                img: 'https://readdy.ai/api/search-image?query=real%20business%20consulting%20meeting%20japanese%20professionals%20in%20suits%20discussing%20strategy%20around%20conference%20table%20with%20documents%20laptops%20and%20charts%20natural%20office%20lighting%20authentic%20corporate%20environment%20realistic%20photography%20professional%20business%20consultation%20scene&width=300&height=256&seq=consultinging12&orientation=squarish',
                title: 'ビジネスコンサルティング',
                delay: 'delay-200'
              },
              {
                bg: 'bg-amber-50',
                img: 'https://readdy.ai/api/search-image?query=real%20digital%20marketing%20workspace%20with%20person%20working%20on%20laptop%20showing%20real%20analytics%20dashboard%20graphs%20and%20social%20media%20platforms%20on%20screen%20modern%20office%20desk%20with%20coffee%20smartphone%20and%20notebooks%20natural%20lighting%20authentic%20work%20environment%20realistic%20photography&width=300&height=256&seq=marketing12&orientation=squarish',
                title: 'デジタルマーケティング',
                delay: 'delay-300'
              },
              {
                bg: 'bg-gray-100',
                img: 'https://readdy.ai/api/search-image?query=vibrant%20corporate%20event%20promotion%20scene%20with%20professional%20exhibition%20booth%20colorful%20banners%20promotional%20materials%20business%20conference%20attendees%20networking%20modern%20event%20space%20bright%20lighting%20engaging%20promotional%20displays%20marketing%20materials%20brochures%20realistic%20photography%20professional%20event%20management&width=300&height=256&seq=promotion1&orientation=squarish',
                title: 'プロモーション',
                delay: 'delay-[400ms]'
              }
            ].map((service, index) => (
              <Link 
                key={index}
                to="/business"
                className={`${service.bg} rounded-xl sm:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-3 cursor-pointer group ${visibleSections.has('services') ? `opacity-100 translate-y-0 ${service.delay}` : 'opacity-0 translate-y-12'}`}
                data-product-shop
              >
                <div className="h-40 sm:h-48 md:h-64 w-full overflow-hidden">
                  <img 
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-3 sm:p-4 md:p-6 bg-white transition-colors duration-300 group-hover:bg-teal-50">
                  <h3 className="text-xs sm:text-sm md:text-lg font-semibold text-center text-gray-800 transition-colors duration-300 group-hover:text-teal-600">{service.title}</h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-10 md:mt-12">
            <Link
              to="/business"
              className="inline-flex items-center gap-2 px-8 sm:px-12 py-4 sm:py-6 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
            >
              <span>すべてのサービスを見る</span>
              <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* お問い合わせセクション */}
      <section id="contact" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white" data-animate>
        <div className="max-w-4xl mx-auto">
          <div className={`text-center mb-8 sm:mb-10 md:mb-12 transition-all duration-1000 ${visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-xs sm:text-sm text-teal-600 tracking-wider mb-2 sm:mb-3">お問い合わせ</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-3 sm:mb-4">
              お気軽にご相談ください
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4">
              ご質問やご相談がございましたら、下記フォームよりお問い合わせください。担当者より折り返しご連絡させていただきます。
            </p>
          </div>

          <div className={`bg-gray-50 rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg transition-all duration-1000 delay-200 ${visibleSections.has('contact') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <form id="contact-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-5 sm:space-y-6">
              <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 text-sm"
                  placeholder="03-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all duration-300 resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500文字</p>
              </div>

              {formStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-xs sm:text-sm">
                  お問い合わせを受け付けました。担当者より折り返しご連絡させていただきます。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-xs sm:text-sm">
                  送信に失敗しました。必須項目をご確認の上、もう一度お試しください。
                </div>
              )}

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
                >
                  {formStatus === 'submitting' ? '送信中...' : '送信する'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gray-100 text-gray-800 py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 mb-8 sm:mb-12">
            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <Link to="/" onClick={handleFooterLogoClick} className="flex items-center gap-2 mb-4">
                <div className="bg-white rounded-lg p-2 sm:p-3">
                  <img 
                    src="https://static.readdy.ai/image/a173b05d2bea81f49058e98548b205ed/610095eea486ea38414ef21e2281d099.png" 
                    alt="株式会社ツーサム" 
                    className="h-16 sm:h-20 md:h-24 w-auto"
                  />
                </div>
              </Link>
              <p className="text-gray-600 text-xs sm:text-sm">ビジネスの可能性を最大化する、共創ソリューション。</p>
            </div>

            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">会社情報</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li><a href="#about" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">会社概要</a></li>
                <li><Link to="/business" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">事業内容</Link></li>
                <li><a href="#contact" className="hover:text-gray-900 transition-all duration-300 cursor-pointer hover:translate-x-1 inline-block">お問い合わせ</a></li>
              </ul>
            </div>

            <div className="transform transition-all duration-300 hover:translate-y-[-4px]">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">お問い合わせ</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span className="break-all">info@twosum.co.jp</span>
                </li>
                <li className="flex items-center gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <span>03-5249-3995</span>
                </li>
                <li className="flex items-start gap-2 transition-all duration-300 hover:text-gray-900">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <span>東京都北区赤羽2-8-1 大洋堂ビル2F</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-gray-300 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left">© 2026 株式会社ツーサム. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
